import { useApi } from "@platform/api";
import { useQuery } from "@tanstack/react-query";

/** How well a census answers one checklist field. */
export type CheckStatus =
  | "complete"
  | "partial"
  | "missing"
  | "unread"
  | "notInCensus";

export type CensusField =
  | "dateOfBirth"
  | "gender"
  | "elections"
  | "class"
  | "homeZip"
  | "salary"
  | "occupation"
  | "electedVolumes"
  | "workZip"
  | "workState"
  | "w2Employees";

export interface CensusFieldCheck {
  field: CensusField;
  status: CheckStatus;
  missingMembers: number;

  /**
   * The census's own column headings this value was read from, one per column
   * the field reads. Many spellings satisfy one column, and they do not all
   * mean the same thing to whoever wrote them, so the heading is what shows a
   * wrong match.
   */
  readFrom?: string[];

  /**
   * The headings to add, one per column no heading in the census matched.
   * Both this and `readFrom` are set where a field reads two columns and the
   * census named only one.
   */
  preferredHeaders?: string[];
}

export type CoverageType =
  | "dental"
  | "vision"
  | "std"
  | "ltd"
  | "basicLife"
  | "voluntaryLife"
  | "adnd";

/**
 * What the census said about one coverage for each employee. The four counts
 * add up to the census's employees.
 */
export interface CoverageEnrollment {
  coverage: CoverageType;
  enrolled: number;
  waived: number;
  /** Cells the parser could not read; the values are in `unreadableValues`. */
  unreadable: number;
  /**
   * Employees the census said nothing about. From a spreadsheet, that is no
   * column for the coverage or one blank all the way down; an Employee
   * Navigator census can leave one employee unstated beside others enrolled.
   */
  notStated: number;
  /** Enrolled lives the broker entered on the RFP; absent where none were. */
  brokerEnrolled?: number;
}

export interface CensusLineCheck {
  line: string;
  checks: CensusFieldCheck[];
  passed: number;
  total: number;
  /** One entry per coverage the line covers; empty for FMLA and state disability. */
  enrollment: CoverageEnrollment[];
}

export interface CensusSourceDetail {
  /** Identifies the read. Changes when a census is read again. */
  ingestionId: number;
  source: string;
  outcome: string;
  /** When the census was read, as an ISO timestamp. */
  readAt?: string;
  /** The file the census was read from. Absent when no file was read. */
  filename?: string;
  /** Opens that file. Absent once the document is deleted or flagged. */
  documentUrl?: string;
  sheetName?: string;
  headerRow?: number;
}

/**
 * One value as written and the sheet rows it sits on.
 *
 * PHI: `value` is a raw census cell. The server withholds it for personal-data
 * columns; nothing here should log or forward it.
 */
export interface UnreadableValueGroup {
  /** Absent for a personal-data column, which is reported by row only. */
  value?: string;
  rows: number[];
}

export interface UnreadableColumn {
  column: string;
  values: UnreadableValueGroup[];
}

export interface CensusValidation {
  members: number;
  employees: number;
  lines: CensusLineCheck[];
  unmappedHeaders: string[];
  coercionFailures?: Record<string, number>;
  /** Where each value counted in `coercionFailures` sits. */
  unreadableValues?: UnreadableColumn[];
  source?: CensusSourceDetail;
}

interface UseCensusValidationReturn {
  validation?: CensusValidation;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Reads what census processing made of an RFP's census.
 *
 * Calls bp-api, which owns the census tables. The response names no member and
 * carries no personal data: an unreadable date of birth, zip or salary arrives
 * as a row number, never as what was written.
 */
function useCensusValidation(rfpId?: string): UseCensusValidationReturn {
  const axiosInstance = useApi();

  const query = useQuery({
    queryKey: ["censusValidation", rfpId],
    queryFn: () =>
      axiosInstance
        .get<CensusValidation>(`/bp/v1/rfps/${rfpId}/census-validation`)
        .then((response) => response.data),
    enabled: !!rfpId,
  });

  return {
    validation: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export { useCensusValidation };
